// ReservationManager.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/http";
import "../../styles/admin/ReservationManager.css";

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? String(dateStr) : d.toLocaleDateString();
}

function formatTime(timeStr) {
  if (!timeStr) return "-";
  return String(timeStr).slice(0, 5);
}

function money(v) {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(2);
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

const ReservationManager = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [err, setErr] = useState("");

  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [selected, setSelected] = useState(null);

  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const parseApiError = useCallback((e, fallback = "Eroare.") => {
    const status = e?.response?.status;
    const data = e?.response?.data;

    if (status === 401 || status === 403) {
      return "Nu ai acces (doar superadmin). Verifică token-ul / permisiunile.";
    }

    if (!data) return e?.message || fallback;
    if (typeof data === "string") return data;
    if (Array.isArray(data)) return data[0] || fallback;
    if (typeof data?.detail === "string") return data.detail;

    if (typeof data === "object") {
      const k = Object.keys(data)[0];
      const v = data[k];
      if (Array.isArray(v)) return v[0] || fallback;
      if (typeof v === "string") return v;
      return JSON.stringify(data);
    }

    return fallback;
  }, []);

  const normalizeList = useCallback((data) => {
    return Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
  }, []);

  const fetchReservations = useCallback(
    async (signal) => {
      setLoading(true);
      setErr("");
      try {
        const res = await api.get("/produs/reservations/admin/", { signal });
        const items = normalizeList(res.data);
        if (!mountedRef.current) return;
        setRows(items);
      } catch (e) {
        if (!mountedRef.current) return;
        console.error(e);
        setRows([]);
        setErr(parseApiError(e, "Eroare la încărcarea rezervărilor."));
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [normalizeList, parseApiError]
  );

  const fetchReservationDetails = useCallback(
    async (id) => {
      setErr("");
      setDetailsLoading(true);

      const controller = new AbortController();
      try {
        const res = await api.get(`/produs/reservations/admin/${id}/`, {
          signal: controller.signal,
        });
        if (!mountedRef.current) return;
        setSelected(res.data);
      } catch (e) {
        if (!mountedRef.current) return;
        console.error(e);
        setErr(parseApiError(e, "Eroare la încărcarea detaliilor rezervării."));
      } finally {
        if (mountedRef.current) setDetailsLoading(false);
      }

      return () => controller.abort();
    },
    [parseApiError]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchReservations(controller.signal);
    return () => controller.abort();
  }, [fetchReservations]);

  const resetFilters = useCallback(() => {
    setQ("");
    setDateFrom("");
    setDateTo("");
  }, []);

  useEffect(() => {
    setPage(1);
  }, [q, dateFrom, dateTo]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const from = dateFrom ? startOfDay(dateFrom) : null;
    const to = dateTo ? endOfDay(dateTo) : null;

    return rows.filter((r) => {
      if (qq) {
        const hay = [r?.prenume, r?.phone, r?.car_name, r?.car_producer, String(r?.id ?? "")]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!hay.includes(qq)) return false;
      }

      if (from || to) {
        const d = r?.from_date ? new Date(r.from_date) : null;
        if (!d || Number.isNaN(d.getTime())) return false;

        if (from && d < from) return false;
        if (to && d > to) return false;
      }

      return true;
    });
  }, [rows, q, dateFrom, dateTo]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  }, [filtered.length]);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const paged = useMemo(() => {
    const startIdx = (page - 1) * PAGE_SIZE;
    return filtered.slice(startIdx, startIdx + PAGE_SIZE);
  }, [filtered, page]);

  const pageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const items = [];
    const add = (x) => items.push(x);
    const addDots = () => items.push("…");

    const left = Math.max(2, page - 1);
    const right = Math.min(totalPages - 1, page + 1);

    add(1);
    if (left > 2) addDots();
    for (let p = left; p <= right; p += 1) add(p);
    if (right < totalPages - 1) addDots();
    add(totalPages);
    return items;
  }, [page, totalPages]);

  const closeDetails = useCallback(() => setSelected(null), []);

  useEffect(() => {
    if (!selected) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDetails();
    };
    document.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [selected, closeDetails]);

  return (
    <div className="sr-resMng">
      <div className="sr-resMng__filters">
        <input
          className="sr-resMng__input sr-resMng__search"
          placeholder="Caută: nume / telefon / mașină / id"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <div className="sr-resMng__date">
          <label className="sr-resMng__label">From date</label>
          <input
            className="sr-resMng__input"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>

        <div className="sr-resMng__date">
          <label className="sr-resMng__label">To date</label>
          <input
            className="sr-resMng__input"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <button className="sr-resMng__btn sr-resMng__btn--secondary" onClick={resetFilters}>
          Reset
        </button>
      </div>

      {err ? <div className="sr-resMng__error">{err}</div> : null}

      {loading ? (
        <div className="sr-resMng__loading">Se încarcă…</div>
      ) : (
        <>
          <div className="sr-resMng__tableWrap">
            <table className="sr-resMng__table">
              <thead>
                <tr>
                  <th className="sr-col--id">ID</th>
                  <th className="sr-col--name">Prenume</th>
                  <th className="sr-col--phone">Telefon</th>
                  <th className="sr-col--date">De la</th>
                  <th className="sr-col--date">Până la</th>
                  <th className="sr-col--car">Mașină</th>

                  <th className="sr-col--num sr-hide-1200">Gaj</th>
                  <th className="sr-col--days sr-hide-1200">Zile</th>
                  <th className="sr-col--num sr-hide-1350">Preț/zi</th>

                  <th className="sr-col--num">Final</th>
                  <th className="sr-col--actions"></th>
                </tr>
              </thead>

              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="sr-resMng__empty">
                      Nu există rezervări pentru filtrele curente.
                    </td>
                  </tr>
                ) : (
                  paged.map((r) => (
                    <tr key={r.id}>
                      <td className="sr-resMng__mono" data-label="ID">
                        {r.id}
                      </td>

                      <td data-label="Prenume" title={r.prenume || ""}>
                        {r.prenume}
                      </td>

                      <td className="sr-resMng__mono" data-label="Telefon" title={r.phone || ""}>
                        {r.phone}
                      </td>

                      <td data-label="De la">
                        <div className="sr-resMng__dt">
                          <span>{formatDate(r.from_date)}</span>
                          <span className="sr-resMng__muted">{formatTime(r.from_time)}</span>
                        </div>
                      </td>

                      <td data-label="Până la">
                        <div className="sr-resMng__dt">
                          <span>{formatDate(r.to_date)}</span>
                          <span className="sr-resMng__muted">{formatTime(r.to_time)}</span>
                        </div>
                      </td>

                      <td data-label="Mașină">
                        <div
                          className="sr-resMng__carMain"
                          title={`${r.car_producer || ""} ${r.car_name || ""}`}
                        >
                          {r.car_producer} {r.car_name}
                        </div>
                      </td>

                      <td
                        className="sr-resMng__right sr-resMng__mono sr-hide-1200"
                        data-label="Gaj"
                      >
                        {money(r.car_gaj)}
                      </td>

                      <td
                        className="sr-resMng__center sr-resMng__mono sr-hide-1200"
                        data-label="Zile"
                      >
                        {r.total_days}
                      </td>

                      <td
                        className="sr-resMng__right sr-resMng__mono sr-hide-1350"
                        data-label="Preț/zi"
                      >
                        {money(r.price_for_day)}
                      </td>

                      <td className="sr-resMng__right sr-resMng__mono" data-label="Final">
                        {money(r.pret_final)}
                      </td>

                      <td className="sr-resMng__right" data-label="Acțiuni">
                        <button
                          className="sr-resMng__btn sr-resMng__btn--primary sr-resMng__btn--small"
                          onClick={() => fetchReservationDetails(r.id)}
                          disabled={detailsLoading}
                          title={detailsLoading ? "Se încarcă..." : "Vezi detalii"}
                        >
                          {detailsLoading ? "..." : "Detalii"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filtered.length > PAGE_SIZE ? (
            <nav className="sr-resMng__pager" aria-label="Pagination">
              <button
                type="button"
                className="sr-resMng__pagerBtn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Prev
              </button>

              <div className="sr-resMng__pagerPages" role="list">
                {pageItems.map((it, idx) =>
                  it === "…" ? (
                    <span key={`dots-${idx}`} className="sr-resMng__pagerDots">
                      …
                    </span>
                  ) : (
                    <button
                      key={it}
                      type="button"
                      className={`sr-resMng__pageNum ${page === it ? "is-active" : ""}`}
                      onClick={() => setPage(it)}
                      aria-current={page === it ? "page" : undefined}
                    >
                      {it}
                    </button>
                  )
                )}
              </div>

              <button
                type="button"
                className="sr-resMng__pagerBtn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </nav>
          ) : null}
        </>
      )}

      {/* ✅ NEW MODAL (în stilul srComModal_*) */}
      {selected ? (
        <div className="srResModal_overlay" onClick={closeDetails}>
          <div className="srResModal_content" onClick={(e) => e.stopPropagation()}>
            <div className="srResModal_header">
              <div className="srResModal_heading">
                Rezervare #{selected.id}
                <span className="srResModal_sub">
                  {selected.created_at ? ` • Creat: ${new Date(selected.created_at).toLocaleString()}` : ""}
                </span>
              </div>

              <button
                className="srResModal_closeBtn"
                type="button"
                onClick={closeDetails}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="srResModal_body">
              <div className="srResModal_grid">
                <div className="srResModal_card">
                  <div className="srResModal_cardTitle">Client</div>

                  <div className="srResModal_kv">
                    <span>Prenume</span>
                    <span>{selected.prenume}</span>
                  </div>
                  <div className="srResModal_kv">
                    <span>Vârsta</span>
                    <span className="srResModal_mono">{selected.virsta}</span>
                  </div>
                  <div className="srResModal_kv">
                    <span>Telefon</span>
                    <span className="srResModal_mono">{selected.phone}</span>
                  </div>
                </div>

                <div className="srResModal_card">
                  <div className="srResModal_cardTitle">Perioadă</div>

                  <div className="srResModal_kv">
                    <span>De la</span>
                    <span className="srResModal_mono">
                      {formatDate(selected.from_date)} {formatTime(selected.from_time)}
                    </span>
                  </div>
                  <div className="srResModal_kv">
                    <span>Până la</span>
                    <span className="srResModal_mono">
                      {formatDate(selected.to_date)} {formatTime(selected.to_time)}
                    </span>
                  </div>
                  <div className="srResModal_kv">
                    <span>Total zile</span>
                    <span className="srResModal_mono">{selected.total_days}</span>
                  </div>
                </div>

                <div className="srResModal_card">
                  <div className="srResModal_cardTitle">Mașină</div>

                  <div className="srResModal_kv">
                    <span>Producător</span>
                    <span>{selected.car_producer}</span>
                  </div>
                  <div className="srResModal_kv">
                    <span>Nume</span>
                    <span>{selected.car_name}</span>
                  </div>
                  <div className="srResModal_kv">
                    <span>Gaj</span>
                    <span className="srResModal_mono">{money(selected.car_gaj)}</span>
                  </div>
                </div>

                <div className="srResModal_card">
                  <div className="srResModal_cardTitle">Costuri</div>

                  <div className="srResModal_kv">
                    <span>Preț/zi</span>
                    <span className="srResModal_mono">{money(selected.price_for_day)}</span>
                  </div>
                  <div className="srResModal_kv">
                    <span>Noapte preluare</span>
                    <span className="srResModal_mono">{money(selected.noapte_preluare)}</span>
                  </div>
                  <div className="srResModal_kv">
                    <span>Noapte returnare</span>
                    <span className="srResModal_mono">{money(selected.noapte_returnare)}</span>
                  </div>

                  <div className="srResModal_kv srResModal_kvStrong">
                    <span>Preț final</span>
                    <span className="srResModal_mono">{money(selected.pret_final)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ReservationManager;